/**
 * Pure XR-frame → wire-sample builders (PROTOCOL.md §3.3, `xr`) — ported from a console
 * spike (xr.js). No React/DOM, no XRSession lifecycle: given an `XRFrame` and a reference
 * space, these just read poses/gamepad state and round them. Kept separate from
 * `session.ts` so they can be unit-tested against plain mock objects shaped like
 * `XRFrame`/`XRInputSource`, without a real WebXR session.
 */

import type { WirePose, XrData, XrHandData } from "../teleop-commands";

/** Reference space the poses are expressed in (sent as `ref`). */
export const XR_REFERENCE_SPACE: XRReferenceSpaceType = "local-floor";

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function poseFromTransform(transform: XRRigidTransform): WirePose {
  const p = transform.position;
  const q = transform.orientation;
  return {
    p: [round3(p.x), round3(p.y), round3(p.z)],
    q: [round3(q.x), round3(q.y), round3(q.z), round3(q.w)],
  };
}

function poseIn(
  frame: XRFrame,
  space: XRSpace | undefined,
  refSpace: XRReferenceSpace
): WirePose | null {
  if (space == null) return null;
  const pose = frame.getPose(space, refSpace);
  return pose ? poseFromTransform(pose.transform) : null;
}

function indicesWhere(buttons: readonly GamepadButton[], pick: (b: GamepadButton) => boolean) {
  const out: number[] = [];
  buttons.forEach((b, i) => {
    if (pick(b)) out.push(i);
  });
  return out;
}

export function buildHeadSnapshot(frame: XRFrame, refSpace: XRReferenceSpace): WirePose | null {
  const viewerPose = frame.getViewerPose(refSpace);
  return viewerPose ? poseFromTransform(viewerPose.transform) : null;
}

export function buildHandSnapshot(
  inputSource: XRInputSource,
  frame: XRFrame,
  refSpace: XRReferenceSpace
): XrHandData {
  const gamepad = inputSource.gamepad;
  const buttons = gamepad ? Array.from(gamepad.buttons) : [];
  return {
    handedness: inputSource.handedness,
    hand: Boolean(inputSource.hand),
    profiles: Array.from(inputSource.profiles ?? []),
    grip: poseIn(frame, inputSource.gripSpace, refSpace),
    ray: poseIn(frame, inputSource.targetRaySpace, refSpace),
    buttons: buttons.map((b) => round3(b.value ?? 0)),
    pressed: indicesWhere(buttons, (b) => b.pressed),
    touched: indicesWhere(buttons, (b) => Boolean(b.touched)),
    axes: gamepad ? Array.from(gamepad.axes, round3) : [],
  };
}

export function buildXrSnapshot(t: number, frame: XRFrame, refSpace: XRReferenceSpace): XrData {
  return {
    frame_t: Math.round(t * 10) / 10,
    ref: XR_REFERENCE_SPACE,
    head: buildHeadSnapshot(frame, refSpace),
    hands: Array.from(frame.session.inputSources, (src) => buildHandSnapshot(src, frame, refSpace)),
  };
}

/** Controller model for the source label (e.g. "meta-quest-touch-plus"), if any. */
export function xrProfilesOf(inputSources: ReadonlyArray<XRInputSource>): string[] {
  const out: string[] = [];
  for (const src of inputSources) {
    const first = src.profiles?.[0];
    if (first && !out.includes(first)) out.push(first);
  }
  return out;
}
